import './RiskScorePanel.css';
import { useECTranslation, localizeResultText } from '../i18n';

interface RiskData {
    composite_score: number;
    band: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    country_risk: number;
    product_sensitivity: number;
    end_use_concern: number;
    recommendation: string;
}

interface RiskScorePanelProps {
    riskData: RiskData | null;
}

function RiskScorePanel({ riskData }: RiskScorePanelProps) {
    const { t, lang } = useECTranslation();
    if (!riskData) return null;

    const { composite_score, band, country_risk, product_sensitivity, end_use_concern, recommendation } = riskData;

    const bandColors: Record<string, { main: string; bg: string; ring: string }> = {
        LOW: { main: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', ring: '#10b981' },
        MEDIUM: { main: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', ring: '#f59e0b' },
        HIGH: { main: '#f97316', bg: 'rgba(249, 115, 22, 0.1)', ring: '#f97316' },
        CRITICAL: { main: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', ring: '#ef4444' },
    };

    const colors = bandColors[band] || bandColors.MEDIUM;
    const gaugePercent = Math.min(composite_score, 100);
    // CSS conic-gradient for the ring gauge
    const gaugeStyle = {
        background: `conic-gradient(${colors.ring} ${gaugePercent * 3.6}deg, #e5e7eb ${gaugePercent * 3.6}deg)`,
    };

    const dimensions = [
        { label: t('risk.countryRisk'), score: country_risk, emoji: '🌍', weight: '40%' },
        { label: t('risk.productSensitivity'), score: product_sensitivity, emoji: '📦', weight: '35%' },
        { label: t('risk.endUseConcern'), score: end_use_concern, emoji: '🎯', weight: '25%' },
    ];

    const barColor = (score: number): string => {
        if (score <= 25) return '#10b981';
        if (score <= 50) return '#f59e0b';
        if (score <= 75) return '#f97316';
        return '#ef4444';
    };

    return (
        <div className="risk-panel">
            <h3 className="risk-panel-title">{t('risk.title')}</h3>

            <div className="risk-panel-body">
                {/* Gauge */}
                <div className="risk-gauge-wrapper">
                    <div className="risk-gauge-ring" style={gaugeStyle}>
                        <div className="risk-gauge-inner">
                            <span className="risk-gauge-score" style={{ color: colors.main }}>
                                {Math.round(composite_score)}
                            </span>
                            <span className="risk-gauge-label">/ 100</span>
                        </div>
                    </div>
                    <span className="risk-band-badge" style={{ backgroundColor: colors.bg, color: colors.main, borderColor: colors.main }}>
                        {band}
                    </span>
                </div>

                {/* Dimension cards */}
                <div className="risk-dimensions">
                    {dimensions.map((dim) => (
                        <div key={dim.label} className="risk-dim-card">
                            <div className="risk-dim-header">
                                <span className="risk-dim-emoji">{dim.emoji}</span>
                                <span className="risk-dim-label">{dim.label}</span>
                                <span className="risk-dim-weight">{dim.weight}</span>
                            </div>
                            <div className="risk-dim-bar-track">
                                <div
                                    className="risk-dim-bar-fill"
                                    style={{ width: `${dim.score}%`, backgroundColor: barColor(dim.score) }}
                                />
                            </div>
                            <div className="risk-dim-score" style={{ color: barColor(dim.score) }}>
                                {dim.score}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Recommendation */}
            <div className="risk-recommendation" style={{ borderLeftColor: colors.main }}>
                {localizeResultText(recommendation, lang)}
            </div>
        </div>
    );
}

export default RiskScorePanel;
